<template>
  <BContentBox>
    <template #content>
      <div v-loading="settingLoading">
        <BLayout
          justify-center
          column
          class="section"
        >
          <BListItem class="title">
            {{ $t(`corpSettingApi.zoom.apiIntegrationSetting.title`) }}
          </BListItem>
          <BListItem
            class="explain"
          >
            {{ $t('corpSettingApi.zoom.apiIntegrationSetting.explain') }}
          </BListItem>
          <div class="mt-100">
            <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.connectStatus') }}：</span>
            <span v-if="isConnect">{{ $t('corpSettingApi.zoom.apiIntegrationSetting.status.connect') }}</span>
            <span v-else>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.status.unconnect') }}</span>
          </div>
          <BListItem
            v-if="isConnect"
            class="mt-50"
          >
            <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.connectUserName') }}：</span>
            <span>{{ zoomSetting?.apiSetting?.connectUserName || '' }}</span>
          </BListItem>
          <BListItem class="mt-300">
            <BBtn
              v-if="isConnect"
              type="primary"
              :disabled="disableConnectBtn"
              @click="unconnect"
            >
              <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.btnDisconnect') }}</span>
            </BBtn>
            <BBtn
              v-else
              type="primary"
              :disabled="settingLoading || disableConnectBtn || !authorize(['owner', 'bpo_guest'])"
              @click="connect"
            >
              <span>{{ $t('corpSettingApi.zoom.apiIntegrationSetting.btnText') }}</span>
            </BBtn>
          </BListItem>
        </BLayout>
      </div>
    </template>
  </BContentBox>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ZoomSetting } from '@/api/openapi';
import { ZoomSettingApiService } from '@/api/user/resources/zoom_setting';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useWait } from '@/composable/vue-wait';
import ZoomSettingStatuses from '@/const/zoom_setting_statuses';
import { bitterAlert } from '@/plugins/BBitterAlert';

const { inject: injectCurrentUser } = useCurrentUserProvider();
const { currentUser, authorize } = injectCurrentUser();
const { doActionWithWait, wait } = useWait();
const i18n = useI18n();

const zoomSetting = ref<ZoomSetting>(null);

onMounted(async () => {
  fetchZoomSetting();
});

const settingLoading = computed(() => {
  return wait.is(['getZoomSettingWait', 'disconnectZoomApiWait', 'updateZoomSettingWait']);
});

const isConnect = computed(() => {
  return zoomSetting.value?.apiSetting?.connectStatus === ZoomSettingStatuses.CONNECTED;
});

const authLink = computed(() => {
  const url = 'https://zoom.us/oauth/authorize';
  const clientId = import.meta.env.VITE_ZOOM_CLIENT_ID;
  const splitHostname = location.hostname.split('.');
  splitHostname.shift();
  const appDomain = splitHostname.join('.');
  const redirectUrl = `https://${currentUser.value.slug}.${appDomain}/va-app/zoom/oauth/callback`;
  return `${url}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUrl}`;
});

const disableEditing = computed(() => {
  return !authorize(['owner', 'bpo_guest']);
});

const disableConnectBtn = computed(() => {
  return disableEditing.value || settingLoading.value || !zoomSetting.value?.useZoomPhone;
});

const fetchZoomSetting = async () => {
  const api = new ZoomSettingApiService();
  await doActionWithWait('getZoomSettingWait', async () => {
    const { data } = await api.getZoomSetting();
    zoomSetting.value = data.zoomSetting;
  });
};

const connect = () => {
  location.href = authLink.value;
};

const unconnect = async () => {
  const check = await bitterAlert.show({
    title: i18n.t('corpSettingApi.zoom.apiIntegrationSetting.cancelLinkConfirmText'),
    text: i18n.t('general.confirmText'),
  });
  if (!check) { return; }
  const api = new ZoomSettingApiService();
  await doActionWithWait('disconnectZoomApiWait', async () => {
    await api.disconnectZoomApi();
    await fetchZoomSetting();
  });
};
</script>

<style lang="scss" scoped>
.explain {
  color: $concrete-dark;
  margin-bottom: 20px;
  white-space: pre-wrap;
}

.token-expire {
  color: $basecolor-error;
}

.general-footer {
  padding-top: $basespace-600;
}

.section {
  margin-bottom: 100px;
  max-width: 500px;
}

.title {
  font-weight: bold;
  margin-bottom: 10px;
}

.switch-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
